import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown } from 'react-bootstrap'

// components
import { XeroContactLiveSearch } from './XeroContactLiveSearch'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { XeroAPIContact } from '../../../../../../back-end/utilities/apiDefinitions'
import { DetailedStripeOrgAccountXeroOrg } from '../settings'
import { AppState } from '../../../../App.d'
import { MessageAction } from '../../../ui/messages/message'

interface PayoutSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	appState: AppState
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	updateMessages: (action: MessageAction) => void
}

const PayoutSettings = (props: PayoutSettingsProps) => {
	return (
		<>
			<Form.Group as={Row}>
				<Form.Label column sm={2}>
					Xero Contact
				</Form.Label>
				{(props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsXeroContact ||
					props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsXeroContactID) && (
					<Col sm={3} className="align-self-center">
						{props.orgMapping.stripeOrgAccountXeroOrgSettings?.rollupsXeroContact?.name ||
							props.mappedXeroOrg!.contacts.find(
								(c) => c.xeroContactID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsXeroContactID
							)?.name}
					</Col>
				)}
				<XeroContactLiveSearch
					onConfirm={(values) => {
						props.handleSettingsUpdate(props.stripeOrgID, {
							rollupsXeroContactXeroID: values.xeroContactID,
							rollupsXeroContact: values.xeroContact,
						})
					}}
					accountXeroOrg={props.mappedXeroOrg!}
					appState={props.appState}
					updateMessages={props.updateMessages}
					disabled={false}
				/>
			</Form.Group>
			<Form.Group as={Row}>
				<Form.Label column>Configure Xero settings for miscellaneous positive payout line items:</Form.Label>
			</Form.Group>
			<Form.Group as={Row}>
				<Form.Label column sm={2}>
					Xero Account
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-rollups-positive-account-select`}
						title={
							props.mappedXeroOrg!.accounts.find(
								(a) => a.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsPositiveXeroAccountID
							)?.name || 'Select Xero Account'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { rollupsPositiveXeroAccountID: eventKey! })}
					>
						<Dropdown.Header>Revenue</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'REVENUE')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Other Income</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'OTHERINCOME')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Sales</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'SALES')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Current Liability</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'CURRLIAB')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-rollups-positive-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsPositiveXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { rollupsPositiveXeroTaxRateID: eventKey! })}
					>
						{props
							.mappedXeroOrg!.taxRates.filter((tr) => tr.canApplyToRevenue)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>

			<Form.Group as={Row} className="mt-4">
				<Form.Label column>Configure Xero settings for miscellaneous negative payout line items:</Form.Label>
			</Form.Group>
			<Form.Group as={Row}>
				<Form.Label column sm={2}>
					Xero Account
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-rollups-positive-account-select`}
						title={
							props.mappedXeroOrg!.accounts.find(
								(a) => a.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsNegativeXeroAccountID
							)?.name || 'Select Xero Account'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { rollupsNegativeXeroAccountID: eventKey! })}
					>
						<Dropdown.Header>Direct Costs</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'DIRECTCOSTS')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Expenses</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'EXPENSE')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Overheads</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'OVERHEADS')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-rollups-positive-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.rollupsNegativeXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { rollupsNegativeXeroTaxRateID: eventKey! })}
					>
						{props
							.mappedXeroOrg!.taxRates.filter((tr) => tr.canApplyToExpenses)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
		</>
	)
}

export { PayoutSettings }
